import { StatusCodes } from "http-status-codes";
import { OPD_BILLING_APIS } from "../utils/constants";
import {
  IOPDBill,
  IOPDBillListFetchResp,
  IOPDBillMapping,
  IOPDBillMappingFetchResp,
  IOPDDoc,
  IOPDDocS3Data,
  IOPDErrorResp,
  IOPDHealthCareBillHeader,
  IOPDHealthCareBillHeaderFetchResp,
  IOPDHealthCareOPDBillItem,
  IOPDHealthCareBillMedicalItem,
  IOPDManualQuestion,
  IOPDPatientPayable,
  IOPDPatientPayableFetchResp,
  IOPDSchemeBillHeader,
  IOPDSchemeBillImplantItem,
  IOPDSchemeOPDBillItem,
  IOPDSchemeBillMedicalItem,
  IOPDSchemeBillPackageItem,
  IOPDSchemeBillPatientPayableItem,
  IOPDSchemeHeaderFetchResp,
  IOPDUploadedDocData,
  IOPDAnsweredQuestion,
  IOPDFetchQuestionAnswersResp,
  IOPDPostQuestionAnswerState,
  IOPDSchemeHeaderUpdate,
  IOPDListFilters,
} from "../interfaces/IOPDBills";
import Axios from "../utils/axiosInterceptor";

export const fetchOPDBills = async (
  hospitalId: number,
  filters: IOPDListFilters
): Promise<IOPDBillListFetchResp> => {
  try {
    const resp = await Axios.post<{
      data: IOPDBillListFetchResp;
      status: number;
    }>(OPD_BILLING_APIS.GET_OPD_BILL_LIST(hospitalId), {
      ...filters,
      pageNumber: filters.pageNumber - 1,
    });
    if (resp.status === StatusCodes.OK) {
      return resp.data;
    } else {
      throw new Error("Cannot fetch opd bills list");
    }
  } catch (error) {
    throw new Error("Cannot fetch opd bills list");
  }
};

export const fetchOPDQuestionnaires = async (
  accessToken: string
): Promise<{
  opdQuestionnaires?: Array<IOPDManualQuestion>;
  errorMessage?: string;
  status: number;
}> => {
  try {
    const response = await fetch(OPD_BILLING_APIS.OPD_QUESTIONNAIRES, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
    });
    const opdQuestionnaires: Array<IOPDManualQuestion> = await response.json();
    if (response.status === StatusCodes.OK) {
      return { opdQuestionnaires, status: response.status };
    } else {
      return {
        errorMessage: "Cannot fetch opd questionnaires list",
        status: response.status,
      };
    }
  } catch (error) {
    throw new Error("Cannot fetch opd questionnaires list");
  }
};

export const uploadOPDDocToServer = async (
  fileObj: object,
  accessToken: string,
  docType: string,
  patientId: string | null,
  caseId: string | null,
  hospitalId: string | null,
  visitId: string | null
): Promise<{
  docUploadResp?: IOPDDoc;
  status: number;
  errorMessage?: string;
  docType: string;
}> => {
  try {
    const formData = new FormData();
    formData.append("file", fileObj as Blob);
    formData.append("hospitalId", hospitalId ?? "");
    formData.append("patientId", patientId ?? "");
    formData.append("caseId", caseId ?? "");
    formData.append("visitId", visitId ?? "");
    formData.append("documentType", docType);

    const response = await fetch(OPD_BILLING_APIS.UPLOAD_DOC, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      body: formData,
    });
    const docUploadResp: IOPDDoc = await response.json();
    if (response.status === StatusCodes.OK) {
      return { docUploadResp, status: response.status, docType };
    } else {
      return {
        errorMessage: "Cannot upload document",
        status: response.status,
        docType,
      };
    }
  } catch (error) {
    throw new Error("Cannot upload document");
  }
};

export const getNewOPDBillMapping = async (
  accessToken: string,
  hospitalId: number,
  documentPaths: Array<IOPDDocS3Data>,
  billMapping: object
): Promise<IOPDBillMappingFetchResp | IOPDErrorResp> => {
  try {
    const response = await fetch(
      OPD_BILLING_APIS.GET_BILL_MAPPING(hospitalId),
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ billMapping, documentPaths }),
      }
    );
    const billMappingResp: {
      billMappingOpd: IOPDBillMapping;
      documentPaths: Array<IOPDUploadedDocData>;
    } = await response.json();
    if (response.status === StatusCodes.OK) {
      return { response: billMappingResp, status: response.status };
    } else {
      return {
        response: null,
        errorMessage: "Cannot get new opd bill",
        status: response.status,
      };
    }
  } catch (error) {
    throw new Error("Cannot get new opd bill");
  }
};

export const getNewOPDBillHealthCareBillHeader = async (
  accessToken: string,
  healthCareBillHeaderId: number
): Promise<IOPDHealthCareBillHeaderFetchResp | IOPDErrorResp> => {
  try {
    const response = await fetch(
      OPD_BILLING_APIS.GET_HEALTHCARE_BILL_HEADER(healthCareBillHeaderId),
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );
    const resp: {
      healthCareOpdBillHeader: IOPDHealthCareBillHeader;
      healthCareOpdBillItems: {
        healthCareOpdBillOpdItems: Array<IOPDHealthCareOPDBillItem>;
        healthCareOpdBillMedicalItems: Array<IOPDHealthCareBillMedicalItem>;
      };
    } = await response.json();
    if (response.status === StatusCodes.OK) {
      return { response: resp, status: response.status };
    } else {
      return {
        response: null,
        errorMessage: "Cannot get healthcare bill headers",
        status: response.status,
      };
    }
  } catch (error) {
    throw new Error("Cannot get healthcare bill headers");
  }
};

export const getHealthCarePatientPayable = async (
  accessToken: string,
  healthCareBillHeaderId: number
): Promise<IOPDPatientPayableFetchResp | IOPDErrorResp> => {
  try {
    const response = await fetch(
      OPD_BILLING_APIS.GET_PATIENT_PAYABLE(healthCareBillHeaderId),
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );
    const resp: IOPDPatientPayable = await response.json();
    if (response.status === StatusCodes.OK) {
      return {
        response: {
          data: resp,
          healthCareBillHeaderId: healthCareBillHeaderId,
        },
        status: response.status,
      };
    } else {
      return {
        response: null,
        errorMessage: "Cannot get healthcare patient payable",
        status: response.status,
      };
    }
  } catch (error) {
    throw new Error("Cannot get healthcare patient payable");
  }
};

export const getOPDQuestionAnswerCollection = async (
  accessToken: string,
  billMappingId: number
): Promise<IOPDFetchQuestionAnswersResp | IOPDErrorResp> => {
  try {
    const response = await fetch(
      OPD_BILLING_APIS.GET_QUESTION_ANSWER_COLLECTION(billMappingId),
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );
    const resp: IOPDPostQuestionAnswerState = await response.json();
    if (response.status === StatusCodes.OK) {
      return {
        response: resp,
        status: response.status,
      };
    } else {
      return {
        response: null,
        errorMessage: "Cannot get opd question answer collection",
        status: response.status,
      };
    }
  } catch (error) {
    throw new Error("Cannot get opd question answer collection");
  }
};

export const emptySchemeBillItem = async (
  accessToken: string,
  schemeBillHeaderId: number | null
): Promise<IOPDSchemeHeaderFetchResp | IOPDErrorResp> => {
  try {
    const response = await fetch(
      OPD_BILLING_APIS.RESET_OPD_SCHEME_ITEM(schemeBillHeaderId),
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );
    const resp: {
      schemeOpdBillHeader: IOPDSchemeBillHeader;
      schemeOpdBillItems: {
        schemeOpdBillImplantItems: Array<IOPDSchemeBillImplantItem>;
        schemeOpdBillPackageItems: Array<IOPDSchemeBillPackageItem>;
        schemeOpdBillOpdItems: Array<IOPDSchemeOPDBillItem>;
        schemeOpdBillOpdItemsPatientPayable: Array<IOPDSchemeBillPatientPayableItem>;
        schemeOpdBillMedicalItems: Array<IOPDSchemeBillMedicalItem>;
        schemeOpdBillMedicalItemsPatientPayable: Array<any>;
      };
    } = await response.json();
    if (response.status === StatusCodes.OK) {
      return { response: resp, status: response.status };
    } else {
      return {
        response: null,
        errorMessage: "Cannot get opd scheme bill header",
        status: response.status,
      };
    }
  } catch (error) {
    throw new Error("Cannot get opd scheme bill header");
  }
};

export const getOPDBillSchemeHeader = async (
  accessToken: string,
  schemeBillHeaderId: number
): Promise<IOPDSchemeHeaderFetchResp | IOPDErrorResp> => {
  try {
    const response = await fetch(
      OPD_BILLING_APIS.GET_SCHEME_BILL_HEADER(schemeBillHeaderId),
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );
    const resp: {
      schemeOpdBillHeader: IOPDSchemeBillHeader;
      schemeOpdBillItems: {
        schemeOpdBillImplantItems: Array<IOPDSchemeBillImplantItem>;
        schemeOpdBillPackageItems: Array<IOPDSchemeBillPackageItem>;
        schemeOpdBillOpdItems: Array<IOPDSchemeOPDBillItem>;
        schemeOpdBillOpdItemsPatientPayable: Array<IOPDSchemeBillPatientPayableItem>;
        schemeOpdBillMedicalItems: Array<IOPDSchemeBillMedicalItem>;
        schemeOpdBillMedicalItemsPatientPayable: Array<any>;
      };
    } = await response.json();
    if (response.status === StatusCodes.OK) {
      return { response: resp, status: response.status };
    } else {
      return {
        response: null,
        errorMessage: "Cannot get opd scheme bill header",
        status: response.status,
      };
    }
  } catch (error) {
    throw new Error("Cannot get opd scheme bill header");
  }
};

export const postQuestionAnswersCollection = async (
  formData: Array<IOPDAnsweredQuestion>
): Promise<IOPDFetchQuestionAnswersResp | IOPDErrorResp> => {
  try {
    const response = await Axios.post(
      OPD_BILLING_APIS.POST_QUESTION_ANSWER_COLLECTION,
      formData
    );

    if (response.status === StatusCodes.OK) {
      return { response: response.data, status: response.status };
    } else {
      return {
        response: null,
        errorMessage: "Cannot post opd questions and answers",
        status: response.status,
      };
    }
  } catch (error) {
    throw new Error("Cannot post opd questions and answers");
  }
};

export const getEditOPDBillMapping = async (
  accessToken: string,
  hospitalId: number,
  opdBillId: string
): Promise<IOPDBillMappingFetchResp | IOPDErrorResp> => {
  try {
    const response = await fetch(
      OPD_BILLING_APIS.GET_EDIT_OPD_BILL_MAPPING(hospitalId, opdBillId),
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );
    const billMappingResp: {
      billMappingOpd: IOPDBillMapping;
      documentPaths: Array<IOPDUploadedDocData>;
    } = await response.json();
    if (response.status === StatusCodes.OK) {
      return { response: billMappingResp, status: response.status };
    } else {
      return {
        response: null,
        errorMessage: "Cannot get edit opd bill",
        status: response.status,
      };
    }
  } catch (error) {
    throw new Error("Cannot get edit opd bill");
  }
};

export const updateSchemeBillItems = async (
  hospitalId: number | undefined,
  billMappingId: number | null,
  schemeBillHeaderId: number | null,
  schemeBillData: IOPDSchemeHeaderUpdate
): Promise<IOPDSchemeHeaderFetchResp | IOPDErrorResp> => {
  try {
    const response = await Axios.put(
      OPD_BILLING_APIS.UPDATE_SCHEME_BILL_ITEMS(
        hospitalId,
        billMappingId,
        schemeBillHeaderId
      ),
      schemeBillData
    );
    if (response.status === StatusCodes.CREATED) {
      return { response: response.data, status: response.status };
    } else {
      return {
        response: null,
        errorMessage: "Cannot update scheme bill items",
        status: response.status,
      };
    }
  } catch (error) {
    throw new Error("Cannot update scheme bill items");
  }
};

export const callApproveOPDBill = async (
  hospitalId: number | undefined,
  currBillMappingId: number | undefined,
  currHealthCareBillHeaderId: number | undefined,
  currSchemeBillHeaderId: number | undefined,
  status: string
): Promise<{ response: string; status: number } | IOPDErrorResp> => {
  try {
    const url = OPD_BILLING_APIS.APPROVE_OPD_BILL(
      hospitalId,
      currBillMappingId,
      currHealthCareBillHeaderId,
      currSchemeBillHeaderId
    );
    const response = await Axios.get(url, {
      params: { status },
    });
    if (response.status === StatusCodes.OK) {
      return { response: response.data, status: response.status };
    } else {
      return {
        response: null,
        errorMessage: "Cannot approve OPD bill",
        status: response.status,
      };
    }
  } catch (error) {
    throw new Error("Cannot approve OPD bill");
  }
};

export const downloadAbstractExcel = async (
  currSchemeBillHeaderId: number | undefined
): Promise<{ response: { s3Url: string }; status: number } | IOPDErrorResp> => {
  try {
    const response = await Axios.get(
      OPD_BILLING_APIS.DOWNLOAD_ABSTRACT_EXCEL(currSchemeBillHeaderId)
    );
    if (response.status === StatusCodes.OK) {
      return { response: response.data, status: response.status };
    } else {
      return {
        response: null,
        errorMessage: "Cannot download abstract excel",
        status: response.status,
      };
    }
  } catch (error) {
    throw new Error("Cannot download abstract excel");
  }
};

export const downloadAbstractZip = async (
  hospitalId: number | undefined,
  dateFrom: string | undefined,
  dateTo: string | undefined
): Promise<{ response: { s3Url: string }; status: number } | IOPDErrorResp> => {
  try {
    const response = await Axios.get(
      OPD_BILLING_APIS.DOWNLOAD_ABSTRACT_ZIP(hospitalId, dateFrom, dateTo)
    );
    if (response.status === StatusCodes.OK) {
      return { response: response.data, status: response.status };
    } else {
      return {
        response: null,
        errorMessage: "Cannot download abstract zip",
        status: response.status,
      };
    }
  } catch (error) {
    throw new Error("Cannot download abstract zip");
  }
};
